import redactPaymentCard from 'lib/redactPaymentCard';
import getCustomerProperties from 'lib/getCustomerProperties';
import getChannel from 'lib/getChannel';
import getTrimmedUrl from 'lib/getTrimmedUrl';
import generateId from 'lib/generateId';

import { trackChatMessageSent } from 'events/analytics';
import messageEvents from 'events/messages';
import sessionEvents from 'events/session';
import messageEnums from 'enums/messages';
import sessionSelectors from 'selectors/session';
import selfServiceSelectors from 'selectors/selfService';
import appSelectors from 'selectors/app';
import userSelectors from 'selectors/user';

async function sendAttachments(attachments, state, isInitialMessage, dispatch, messageGateway) {
  let attachmentMetadataItems = [];
  let pendingAttachmentPayloads = [];

  for (let i = 0; i < attachments.length; i++) {
    const metadata = attachments[i].fileType.includes('image')
      ? createMetadata(state, isInitialMessage, messageEnums.SUB_TYPES.IMAGE)
      : createMetadata(state, isInitialMessage, messageEnums.SUB_TYPES.FILE);
    metadata.fileName = attachments[i].file.name;
    attachmentMetadataItems.push(metadata);

    const attachmentPayload = createMessagePayload(
      metadata.internalId,
      { fileName: metadata.fileName, mediaUrl: attachments[i].blob },
      Date.now(),
      metadata.subType
    );
    pendingAttachmentPayloads.push(attachmentPayload);
    dispatch(messageEvents.createMessageSending(attachmentPayload));
    isInitialMessage = false;
  }

  for (let i = 0; i < attachments.length; i++) {
    await messageGateway.uploadFile(attachments[i].file, attachmentMetadataItems[i]);
  }
}

export default function sendMessage(message = '', attachments = []) {
  if (message) {
    message = redactPaymentCard(message);
  }

  return async (dispatch, getState, { messageGateway }) => {
    const state = getState();
    const type = 'text';
    let isInitialMessage = !sessionSelectors.hasActiveSession(state.session);
    const metadata = createMetadata(state, isInitialMessage, messageEnums.SUB_TYPES.TEXT);
    let sentAt = Date.now();
    const messagePayload = createMessagePayload(
      metadata.internalId,
      { body: message },
      sentAt,
      messageEnums.SUB_TYPES.TEXT
    );

    // TODO: [Albert] this previously was in the smooch message gateway and didnt belong there
    // i havent found a great place for this yet so im putting it here for now
    // it should probably be powered by the session state vs. comparing message timestamps
    if (isInitialMessage) {
      const sessionUpdate = { isEnded: false, customerUrl: getTrimmedUrl(), endedReason: '' };
      dispatch(sessionEvents.sessionUpdateSuccess(sessionUpdate));
    }

    dispatch(
      trackChatMessageSent({ message, messageId: messagePayload.id, isInitialMessage, type: messagePayload.subType })
    );
    if (message) {
      isInitialMessage = false;
      // we have to wait for smooch to confirm success delivery via webhook
      // so message success will get handled by the smooch callback later
      // so just send the message
      dispatch(messageEvents.createMessageSending(messagePayload));
      await messageGateway.sendMessage(message, metadata, type, sentAt);
    }
    await sendAttachments(attachments, state, isInitialMessage, dispatch, messageGateway);
  };
}

export function createMetadata(state, isInitialMessage, subType) {
  const appId = appSelectors.getAppId(state.app);
  const internalId = generateId();
  const gladlyCustomerId = userSelectors.getGladlyCustomerId(state.user);
  const { browser, device } = getCustomerProperties();
  const deviceId = appSelectors.getDeviceId(state);
  const platform = appSelectors.getPlatform(state);
  const deviceManufacturer = appSelectors.getDeviceManufacturer(state);
  const deviceModel = appSelectors.getDeviceModel(state);
  const os = appSelectors.getOs(state);
  const osVersion = appSelectors.getOsVersion(state);
  const sdkVersion = appSelectors.getSdkVersion(state);
  const channel = getChannel(state);

  const metadata = {
    appId,
    browser,
    channel,
    device,
    deviceId,
    deviceManufacturer,
    deviceModel,
    internalId,
    gladlyCustomerId,
    isInitialMessage,
    os,
    osVersion,
    platform,
    subType,
    sdkVersion,
    url: getTrimmedUrl(),
  };

  if (isInitialMessage) {
    // we have to serialize the timeline bc smooch does not support arrays/objects
    metadata.timeline = JSON.stringify(selfServiceSelectors.getFinalizedTimeline(state.selfService));
  }

  return metadata;
}

export function createMessagePayload(id, content, sentAt, subType) {
  return {
    id,
    content,
    date: sentAt,
    isUser: true,
    subType,
  };
}
